import React, {
  useState,
  useRef,
  useEffect,
  useCallback,
  useMemo,
} from "react";
import {
  useParams,
  useNavigate,
  useHistory,
  useSearchParams,
} from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { selectVideos } from "../../store/videosSlice";
import "./VideoPage.css";
import { onTokenRefreshed } from "../../api/session";
import arrowDown from "../../assets/arrowDown.svg";
import shareIcon from "../../assets/shareIcon.svg";
import BackButton from "../BackButton/BackButton";
import VideoControls from "../VideoControls/VideoControls";
import CommentsLayout from "../CommentsBlock/CommentsLayout";
import VideoWithHLS from "../VideoWithHls/VideoWithHls";
import SharePopup from "./SharePopup";
import UsersPopup from "./UsersPopup";
import BacklogPopup from "./BacklogPopup";
import {
  createVersionComment,
  deleteVersionComment,
  fetchProjectUsers,
  fetchVersion,
  fetchVersionComments,
  forceDeleteVersionComment,
  restoreVersionComment,
  updateVersionComment,
} from "../../store/projectsSlice";
import {
  readOutbox,
  writeOutbox,
  readDraft,
  isDraftEmpty,
} from "../../helpers/commentStorage";
import Avatar from "../Avatar/Avatar";
import {
  currentOrganizationOwnerSelector,
  currentOrganizationSelector,
  currentUploadingVideosSelector,
  organizationUsersSelector,
  redirectingFromSharingSelector,
  userSelector,
} from "../../store/selectors";
import { useTranslation } from "react-i18next";
import {
  processShareToken,
  processShortShareToken,
} from "../../store/usersSlice";
import { useAuth } from "../../context/AuthContext";
import ReleaseNotesModal from "../ReleaseNotesModal/ReleaseNotesModal";
import InfoModal from "../InfoModal/InfoModal";
import {
  fetchCommentReplies,
  setCurrentUploadingVideosInfo,
  setRedirectingFromSharing,
  setRedirectUrl,
} from "../../store/appSlice";
import VideoTimeline from "../VideoTimeline/VideoTimeline";
import { ClickAwayListener } from "@mui/material";
import useIsMobile from "../../hooks/useIsMobile";
import CommentToast from "../hover-preview/CommentToast.jsx";
import { useCommentToasts } from "../hover-preview/useCommentToasts.js";
import PlayerToggles, {
  usePersistedToggle,
} from "../hover-preview/PlayerToggles.jsx";
import toast from "react-hot-toast";
import {
  clearPosition,
  formatClock,
  MIN_POSITION_SEC,
  readPosition,
  resumeTarget,
  savePosition,
} from "../../helpers/playbackPosition";

const RESUME_TOAST_ID = "playback-resume";
const LIST_REFRESH_ATTEMPTS = 3;

function VideoPage() {
  const { versionId } = useParams();
  const [searchParams] = useSearchParams();
  const { sharedToken, showReleaseNotes, setShowReleaseNotes } = useAuth();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const isMobile = useIsMobile();
  const user = useSelector(userSelector);
  const currentOrganization = useSelector(currentOrganizationSelector);
  const [newMarkerInfo, setNewMarkerInfo] = useState(null);
  const organizationUsers = useSelector(organizationUsersSelector);
  const [isSharedChild, setIsSharedChild] = useState(false);
  const [selectedTag, setSelectedTag] = useState({ name: "Все", id: -1 });
  const [tagsArray, setTagsArray] = useState([]);
  const [projectUsers, setProjectUsers] = useState([]);
  const videos = useSelector(selectVideos);
  const { t, i18n } = useTranslation();
  const [video, setVideo] = useState(null);
  const [speed, setSpeed] = useState(1);
  const [currentTime, setCurrentTime] = useState(0);
  const [totalTime, setTotalTime] = useState(0);
  const orgOwner = useSelector(currentOrganizationOwnerSelector);
  const [currentProgress, setCurrentProgress] = useState(0);
  const [isWideMode, setIsWideMode] = useState(false);
  const [commentsArray, setCommentsArray] = useState([]);
  const [pendingComments, setPendingComments] = useState([]);
  const [isOutboxHydrated, setIsOutboxHydrated] = useState(false);
  const [editPrefill, setEditPrefill] = useState(null);
  const [videoControls, setVideoControls] = useState(null);
  const [playbackStarted, setPlaybackStarted] = useState(null);
  const playerRef = useRef(null);
  const [isSharePopupOpen, setSharePopupOpen] = useState(false);
  const [isUsersPopupOpen, setUsersPopupOpen] = useState(false);
  const [isBacklogPopupOpen, setBacklogPopupOpen] = useState(false);
  const [markerPoint, setMarkerPoint] = useState(null);
  const [isLocationMode, setIsLocationMode] = useState(false);
  const [draftDrawing, setDraftDrawing] = useState(null);
  const [isDrawingMode, setIsDrawingMode] = useState(false);
  const [drawingColor, setDrawingColor] = useState("#FF3B30");
  const [activeAnnotation, setActiveAnnotation] = useState(null);
  const [replies, setReplies] = useState({});
  const [commentsFilter, setCommentsFilter] = useState("all");
  const [isShowUnreplied, setIsShowUnreplied] = useState(false);
  const [highlightCommentId, setHighlightCommentId] = useState(null);
  const [currentTags, setCurrentTags] = useState([]);
  const [isShowReleaseNotes, setIsShowReleaseNotes] = useState(false);
  const redirectingFromSharing = useSelector(redirectingFromSharingSelector);
  const [redirectedFromShare, setRedirectedFromShare] = useState(false);
  const [currentHoveredCommentId, setCurrentHoveredCommentId] = useState(null);
  const [newCommentEnd, setNewCommentEnd] = useState(null);
  const [isVideoWaiting, setIsVideoWaiting] = useState(false);
  const [isSendingComment, setIsSendingComment] = useState(false);
  const [myProjectRole, setMyProjectRole] = useState(null);
  const [sharedOrgId, setSharedOrgId] = useState(null);
  const [isSharedNeedLoginVisible, setIsSharedNeedLoginVisible] =
    useState(false);
  const [isViewer, setIsViewer] = useState(false);
  const isShared = !!sharedToken;
  const currentUploadingVideosInfo = useSelector(
    currentUploadingVideosSelector
  );
  const isViewerRole =
    isViewer || (myProjectRole && myProjectRole.role === "VIEWER");
  const isCommenterRole = myProjectRole && myProjectRole.role === "COMMENTER";

  const [tagsDefaultColors, setTagsDefaultColors] = useState({});
  const [isPlaying, setIsPlaying] = useState(false);
  const fullscreenRef = useRef(null);
  const [toastsEnabled, setToastsEnabled] = usePersistedToggle(
    "7cut.player.toasts",
    true
  );
  const [autoScroll, setAutoScroll] = usePersistedToggle(
    "7cut.player.autoscroll",
    true
  );

  const { toasts, dismiss, hold, release } = useCommentToasts({
    comments: commentsArray,
    currentTime,
    isPlaying,
    enabled: toastsEnabled,
    onFire: (c) => {
      if (autoScroll) setHighlightCommentId(c.id);
    },
  });

  const handleLocationClick = (point) => {
    setMarkerPoint(point);
    setIsLocationMode(false);
  };

  const hasDraftAnnotation =
    !!markerPoint || !!(draftDrawing && draftDrawing.strokes?.length);
  const shownAnnotation = hasDraftAnnotation
    ? { markerPoint, drawing: draftDrawing }
    : activeAnnotation;

  const calculateCurrentTags = (commentsArray) => {
    const arr = commentsArray || [];
    const resArray = arr.reduce((acc, current) => {
      if (current.tags.length) {
        return [...acc, ...current.tags.map((tag) => tag.id)];
      }
      return acc;
    }, []);
    setCurrentTags(resArray);
  };

  useEffect(() => {
    const redirectUrl = localStorage.getItem("redirectUrl");
    if (redirectUrl) {
      localStorage.removeItem("redirectUrl");
      navigate(redirectUrl);
    }
  }, []);

  useEffect(() => {
    if (!versionId) return;
    const restored = readOutbox(versionId).map((item) => ({
      ...item,
      status: "failed",
      timecode: formatPendingTimecode(item.timecode),
    }));
    setPendingComments(restored);
    setIsOutboxHydrated(true);
  }, [versionId]);

  useEffect(() => {
    if (!versionId || !isOutboxHydrated) return;
    writeOutbox(versionId, pendingComments);
  }, [versionId, isOutboxHydrated, pendingComments]);

  useEffect(() => {
    const uploadingVideo = currentUploadingVideosInfo.find(
      (uploadingVid) => uploadingVid.data.id === +versionId
    );
    if (uploadingVideo && uploadingVideo.isCompleted) {
      const vidIndex = currentUploadingVideosInfo.findIndex(
        (vid) => vid.data.id === versionId
      );
      const newUploadingVideosArray = [...currentUploadingVideosInfo];
      newUploadingVideosArray.splice(vidIndex, 1);
      dispatch(setCurrentUploadingVideosInfo(newUploadingVideosArray));
    }
  }, []);

  useEffect(() => {
    if (redirectingFromSharing) {
      setRedirectedFromShare(true);
    }
    if (window.location.href.includes("role=viewer")) {
      setIsViewer(true);
    } else {
      setIsViewer(false);
    }
    if (sharedToken) {
      const processAction =
        sharedToken.length < 20 ? processShortShareToken : processShareToken;
      dispatch(processAction(sharedToken)).then((res) => {
        if (!res.error) {
          if (res.payload.organizationId) {
            setSharedOrgId(res.payload.organizationId);
          }
          if (res.payload.id !== +versionId) {
            setIsSharedChild(true);
            dispatch(fetchVersion(versionId)).then((res2) => {
              const title = res2.payload.version;
              document.title = title;
              const videoRes = res2.payload;

              setVideo(videoRes);
              dispatch(
                fetchVersionComments({
                  versionId,
                  isDeleted: commentsFilter === "archived",
                })
              ).then((res) => {
                setCommentsArray(res.payload);
                calculateCurrentTags(res.payload);
              });
            });
          } else {
            setVideo(res.payload);
            setCommentsArray(res.payload.comments);
            if (showReleaseNotes && res.payload.releaseNotes) {
              setIsShowReleaseNotes(true);
            }
            dispatch(setRedirectingFromSharing(false));
          }
        }
      });

      return;
    }

    dispatch(fetchVersion(versionId)).then((res) => {
      const title = res.payload && res.payload.version;
      document.title = title;
      const videoRes = res.payload;
      const projectId = videoRes.projectId;
      setVideo(videoRes);
      if (redirectingFromSharing) {
        if (showReleaseNotes && videoRes.releaseNotes) {
          setIsShowReleaseNotes(true);
        }
        dispatch(setRedirectingFromSharing(false));
      }
      dispatch(fetchProjectUsers(projectId)).then((res) => {
        if (!res.error) {
          let usersArray = res.payload;
          let myRole = usersArray.find((usr) => usr.userId === user.userId);
          if (!myRole && user) {
            const idsToCheck = videoRes.path;
            for (let i = idsToCheck.length - 1; i >= 0; i--) {
              const projectId = idsToCheck[i].id;
              const parentRole = user.projectRoles.find(
                (role) => role.projectId === projectId
              );
              if (parentRole) {
                myRole = {
                  id: parentRole.id,
                  role: parentRole.role,
                  roleSourceProjectId: parentRole.projectId,
                };
                break;
              }
            }
          }
          if (myRole) {
            setMyProjectRole(myRole);
          }

          const filteredArray = usersArray.filter((usr) => {
            if (!usr.allowedContentVersionIds) return true;
            if (!usr.allowedContentVersionIds.length) return true;
            if (!videoRes) return false;
            return usr.allowedContentVersionIds.includes(videoRes.id);
          });

          let rolesToDelete = [];

          for (let i = 0; i < filteredArray.length; i++) {
            const roleToProcess = filteredArray[i];
            if (!!roleToProcess.allowedContentVersionIds.length) continue;
            const versionPrioritizedRole = filteredArray.find(
              (role) =>
                role.email === roleToProcess.email &&
                role.allowedContentVersionIds.includes(+versionId)
            );
            if (!!versionPrioritizedRole) {
              rolesToDelete.push(roleToProcess.id);
            }
          }

          const finalFilteredArray = filteredArray.filter(
            (role) => !rolesToDelete.includes(role.id)
          );
          setProjectUsers(finalFilteredArray);
        }
      });
    });
    dispatch(
      fetchVersionComments({
        versionId,
        isDeleted: commentsFilter === "archived",
      })
    ).then((res) => {
      setCommentsArray(res.payload);
      calculateCurrentTags(res.payload);
    });
  }, []);

  useEffect(() => {
    if (sharedToken) {
      return;
    }
    dispatch(
      fetchVersionComments({
        versionId,
        isDeleted: commentsFilter === "archived",
      })
    ).then((res) => {
      setCommentsArray(res.payload);
      calculateCurrentTags(res.payload);
    });
  }, [commentsFilter]);

  const changeCommentsFilter = (value) => {
    setCommentsFilter(value);
  };

  const pendingCommentsRef = useRef([]);
  const sendingIdsRef = useRef(new Set());
  useEffect(() => {
    pendingCommentsRef.current = pendingComments;
  }, [pendingComments]);

  const handleRetryPendingComment = (localId, { silent = false } = {}) => {
    if (sendingIdsRef.current.has(localId)) return;
    const item = pendingCommentsRef.current.find(
      (comment) => comment.id === localId
    );
    if (!item || item.status !== "failed") return;

    if (typeof navigator !== "undefined" && navigator.onLine === false) {
      if (!silent) toast.error(t("comments.pending.offline"));
      return;
    }

    sendingIdsRef.current.add(localId);

    setPendingComments((prev) =>
      prev.map((comment) =>
        comment.id === localId
          ? { ...comment, status: "sending", error: null }
          : comment
      )
    );

    dispatch(
      createVersionComment({ versionId, commentData: item.pendingPayload })
    )
      .then((res) => {
        if (res.error) failPendingComment(localId, { silent });
        else settlePendingComment(localId);
      })
      .finally(() => {
        sendingIdsRef.current.delete(localId);
      });
  };

  useEffect(() => {
    const retryFailed = () => {
      pendingCommentsRef.current
        .filter((comment) => comment.status === "failed")
        .forEach((comment) =>
          handleRetryPendingComment(comment.id, { silent: true })
        );
    };

    window.addEventListener("online", retryFailed);
    const unsubscribeToken = onTokenRefreshed(retryFailed);

    return () => {
      window.removeEventListener("online", retryFailed);
      unsubscribeToken();
    };
  }, []);

  useEffect(() => {
    const updateControls = () => {
      if (playerRef.current) {
        setVideoControls({
          ...playerRef.current.controls,
          toggleWide: () => {
            const newWideMode = !isWideMode;
            setIsWideMode(newWideMode);
            return newWideMode;
          },
        });
      }
    };
    updateControls();
  }, [playerRef.current, isWideMode]);

  const filteredTags = tagsArray.filter((tag) => currentTags.includes(tag.id));

  const safeCommentTimecode = (timecode) => {
    if (timecode === null || timecode === undefined || timecode === "") {
      return "";
    }
    return formatPendingTimecode(timecode);
  };
  const toSeconds = (value) => {
    const number = Number(value);
    return Number.isFinite(number) && number >= 0 ? number : 0;
  };

  const normalizeTimecode = (timecode) => {
    if (typeof timecode === "string" && timecode.includes(" - ")) {
      const [start, end] = timecode.split(" - ");
      return `${toSeconds(start)} - ${toSeconds(end)}`;
    }
    return toSeconds(timecode);
  };

  const formatPendingTimecode = (timecode) => `${normalizeTimecode(timecode)}`;
  const mergedComments = useMemo(
    () =>
      [...pendingComments, ...commentsArray].map((comment) => ({
        ...comment,
        timecode: safeCommentTimecode(comment.timecode),
      })),
    [pendingComments, commentsArray]
  );

  const buildPendingComment = (commentData, localId) => ({
    ...commentData,
    id: localId,
    localId,
    status: "sending",
    error: null,
    reactions: [],
    tags: (tagsArray || []).filter((tag) =>
      (commentData.tagIds || []).includes(tag.id)
    ),
    mentions: [],
    repliesCount: 0,
    timecode: formatPendingTimecode(commentData.timecode),
    authorId: user.userId,
    author: {
      id: user.userId,
      firstName: user.firstName,
      lastName: user.lastName,
      email: user.email,
      avatar: user.avatar,
    },
    createdDate: new Date().toISOString(),
    pendingPayload: commentData,
  });

  const settlePendingComment = (localId) => {
    const fetchList = (attempt = 0) => {
      dispatch(
        fetchVersionComments({
          versionId,
          isDeleted: commentsFilter === "archived",
        })
      ).then((result) => {
        if (!result.error) {
          setCommentsArray(result.payload);
          calculateCurrentTags(result.payload);
          setPendingComments((prev) =>
            prev.filter((item) => item.id !== localId)
          );
          return;
        }

        if (attempt < LIST_REFRESH_ATTEMPTS - 1) {
          setTimeout(() => fetchList(attempt + 1), 1000 * (attempt + 1));
          return;
        }
        setPendingComments((prev) =>
          prev.filter((item) => item.id !== localId)
        );
        toast.error(t("comments.pending.syncFailed"));
      });
    };

    fetchList();
  };

  const failPendingComment = (localId, { silent = false } = {}) => {
    setPendingComments((prev) =>
      prev.map((item) =>
        item.id === localId
          ? { ...item, status: "failed", error: "send-error" }
          : item
      )
    );
    if (silent) return;
    toast.error(t("comments.pending.sendFailed"));
  };

  const updateComments = () => {
    dispatch(
      fetchVersionComments({
        versionId,
        isDeleted: commentsFilter === "archived",
      })
    ).then((res) => {
      setCommentsArray(res.payload);
    });
  };

  const updateVideo = () => {
    dispatch(fetchVersion(versionId)).then((res) => {
      const title = res.payload && res.payload.version;
      document.title = title;
      const videoRes = res.payload;
      const projectId = videoRes.projectId;
      setVideo(videoRes);
    });
  };

  const lastPositionSaveRef = useRef(0);
  const currentTimeRef = useRef(0);
  const totalTimeRef = useRef(0);
  const resumeAppliedRef = useRef(false);
  const wasPlayingRef = useRef(false);
  const resumeHoldRef = useRef(null);

  useEffect(() => {
    currentTimeRef.current = currentTime;
    totalTimeRef.current = totalTime;
  }, [currentTime, totalTime]);

  const savePlaybackPosition = useCallback(
    (time, duration) => {
      if (resumeHoldRef.current !== null) {
        if (Math.abs(time - resumeHoldRef.current) <= 1.5) return;
        resumeHoldRef.current = null;
      }
      savePosition(versionId, time, duration);
      lastPositionSaveRef.current = Date.now();
    },
    [versionId]
  );

  const maybeSavePosition = useCallback(
    (time) => {
      if (!Number.isFinite(time) || time <= 0) return;
      if (time < MIN_POSITION_SEC) {
        savePlaybackPosition(time, totalTime);
        return;
      }

      if (Date.now() - lastPositionSaveRef.current < 2000) return;

      savePlaybackPosition(time, totalTime);
    },
    [totalTime, savePlaybackPosition]
  );

  useEffect(() => {
    if (isPlaying) {
      resumeHoldRef.current = null;
    } else if (wasPlayingRef.current) {
      savePlaybackPosition(currentTimeRef.current, totalTimeRef.current);
    }
    wasPlayingRef.current = isPlaying;
  }, [isPlaying, savePlaybackPosition]);

  useEffect(() => {
    const saveNow = () =>
      savePlaybackPosition(currentTimeRef.current, totalTimeRef.current);

    const handleVisibilityChange = () => {
      if (document.visibilityState === "hidden") saveNow();
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);
    window.addEventListener("pagehide", saveNow);

    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      window.removeEventListener("pagehide", saveNow);
      saveNow();
    };
  }, [savePlaybackPosition]);

  const handleTimeUpdate = useCallback(
    (time) => {
      if (typeof time === "number" && !isNaN(time) && time >= 0) {
        setCurrentTime(time);
        if (totalTime > 0) {
          setCurrentProgress((time / totalTime) * 100);
        }
        maybeSavePosition(time);
      }
    },
    [totalTime, maybeSavePosition]
  );

  useEffect(() => {
    if (resumeAppliedRef.current) return;
    if (!versionId || !totalTime || totalTime <= 0) return;
    if (!videoControls || typeof videoControls.seek !== "function") return;

    resumeAppliedRef.current = true;

    const explicitTime = Number(searchParams.get("t"));
    const hasExplicitTime = Number.isFinite(explicitTime) && explicitTime > 0;

    if (!hasExplicitTime) {
      const draft = readDraft(versionId);
      const draftTime = Number(String(draft?.timecode ?? "").split(" - ")[0]);
      if (
        draft &&
        !isDraftEmpty(draft) &&
        Number.isFinite(draftTime) &&
        draftTime > 0
      ) {
        videoControls.seek(Math.min(draftTime, Math.max(0, totalTime - 0.5)));
        return;
      }

      const saved = readPosition(versionId);
      if (!saved) return;

      const target = Math.min(resumeTarget(saved), Math.max(0, totalTime - 5));

      resumeHoldRef.current = target;
      videoControls.seek(target);

      toast(
        <div className="resume-toast">
          <span className="resume-toast-text">
            {t("video.resumeFrom", { time: formatClock(saved.t) })}
          </span>
          <button
            type="button"
            className="resume-toast-action"
            onClick={() => {
              resumeHoldRef.current = null;
              clearPosition(versionId);
              videoControls.seek(0);
              toast.dismiss(RESUME_TOAST_ID);
            }}
          >
            {t("video.resumeFromStart")}
          </button>
        </div>,
        {
          id: RESUME_TOAST_ID,
          duration: 4000,
          position: "bottom-center",
        }
      );
      return;
    }

    videoControls.seek(Math.min(explicitTime, Math.max(0, totalTime - 5)));
  }, [versionId, videoControls, totalTime, t, searchParams]);

  const getVideoTitle = () => {
    if (!video) return "Loading...";
    if (!video.path) return video.version;
    const videoPath = video.path;
    let videoTitle = videoPath.reduce((res, current) => {
      res += `${current.title} • `;
      return res;
    }, "");

    return (
      <div
        style={{
          display: "flex",
          flexDirection: "row",
          gap: 8,
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        {videoPath.map((entry) => (
          <>
            <span
              onClick={() => navigate(`/projects/${entry.id}`)}
              className="version-title-entry"
            >{`${entry.title}`}</span>
            <span> • </span>
          </>
        ))}
        <span
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            if (!isShared && !isCommenterRole && !isViewerRole) {
              setSharePopupOpen((v) => !v);
            }
          }}
          className="version-title-entry"
        >{`${t("video.version")} ${video.version}`}</span>
      </div>
    );

    videoTitle += `${t("video.version")} ${video.version}`;
    return videoTitle;
  };

  const handleDurationChange = useCallback(
    (duration) => {
      if (typeof duration === "number" && !isNaN(duration) && duration > 0) {
        setTotalTime(duration);
        if (currentTime >= 0) {
          setCurrentProgress((currentTime / duration) * 100);
        }
      }
    },
    [currentTime]
  );

  if (!video) {
    return (
      <div className="App">
        <div className="mainContent">
          <div className="leftContent">
            <div className="videoPageHeader">
              <BackButton
                onClick={() => {
                  if (redirectedFromShare) {
                    navigate("/home");
                  } else {
                    navigate(-1);
                  }
                }}
              />
              <div
                style={{
                  flex: 1,
                  textAlign: "center",
                  color: "white",
                  fontSize: "16px",
                  fontWeight: 500,
                  fontFamily: "NT Somic, sans-serif",
                }}
              >
                Loading...
              </div>
              <div style={{ width: "56px" }} />
            </div>
          </div>
        </div>
      </div>
    );
  }

  const createOptimisticComment = async (commentData) => {
    const { replacePendingId, ...payloadForServer } = commentData;

    if (replacePendingId) {
      setPendingComments((prev) =>
        prev.filter((item) => item.id !== replacePendingId)
      );
    }

    const localId = `tmp-${Date.now()}-${Math.random()
      .toString(36)
      .slice(2, 8)}`;

    setPendingComments((prev) => [
      ...prev,
      buildPendingComment(payloadForServer, localId),
    ]);

    const res = await dispatch(
      createVersionComment({ versionId, commentData: payloadForServer })
    );

    if (res.error) {
      failPendingComment(localId);
    } else {
      settlePendingComment(localId);
    }

    return res;
  };

  const handleCreateComment = async (commentData, isEdit) => {
    setIsSendingComment(true);
    if (isEdit) {
      const commentId = commentData.id;
      let finalCommentData = {
        content: commentData.content,
        tagIds: commentData.tagIds || [],
        mentions: commentData.mentions || [],
        authorId: commentData.authorId,
        timecode: normalizeTimecode(commentData.timecode),
        extractMentions: false,
        attachments: commentData.attachments || [],
      };

      if (commentData.markerPoint) {
        finalCommentData = {
          ...finalCommentData,
          markerPoint: commentData.markerPoint || null,
        };
      }

      if (commentData.drawing) {
        finalCommentData = {
          ...finalCommentData,
          drawing: commentData.drawing || null,
        };
      }

      const res = await dispatch(
        updateVersionComment({ commentId, commentData: finalCommentData })
      );

      setIsSendingComment(false);

      if (res.error) {
        toast.error(t("comments.pending.editSaveFailed"));
        return res;
      }

      if (!res.error) {
        const res2 = await dispatch(
          fetchVersionComments({
            versionId,
            isDeleted: commentsFilter === "archived",
          })
        );

        if (!res2.error) {
          const newCommentsArray = res2.payload;
          setCommentsArray(newCommentsArray);
          calculateCurrentTags(newCommentsArray);
          let newReplies = {};
          for (let i = 0; i < newCommentsArray.length; i++) {
            let commentId = newCommentsArray[i].id;
            const result = await dispatch(fetchCommentReplies(commentId));
            if (!result.err) {
              const commentReplies = result.payload;
              newReplies[commentId] = commentReplies;
            }
          }
          setReplies(newReplies);
        }
      }
      return res;
    }

    createOptimisticComment(commentData);
    setIsSendingComment(false);

    // const { replacePendingId, ...payloadForServer } = commentData;

    // if (replacePendingId) {
    //   setPendingComments((prev) =>
    //     prev.filter((item) => item.id !== replacePendingId)
    //   );
    // }

    // const localId = `tmp-${Date.now()}-${Math.random()
    //   .toString(36)
    //   .slice(2, 8)}`;

    // setPendingComments((prev) => [
    //   ...prev,
    //   buildPendingComment(payloadForServer, localId),
    // ]);

    // dispatch(
    //   createVersionComment({ versionId, commentData: payloadForServer })
    // ).then((res) => {
    //   if (res.error) {
    //     failPendingComment(localId);
    //   } else {
    //     settlePendingComment(localId);
    //   }
    // });

    // setIsSendingComment(false);
  };

  const handleSubmitCommentsList = async (commentDataList) => {
    if (!commentDataList || !commentDataList.length) return;

    setIsSendingComment(true);
    try {
      for (const commentData of commentDataList) {
        await createOptimisticComment(commentData);
      }
    } finally {
      setIsSendingComment(false);
    }
  };

  const handleDeletePendingComment = (localId) => {
    setPendingComments((prev) =>
      prev.filter((comment) => comment.id !== localId)
    );
  };

  const onEditPendingComment = (comment) => {
    const payload = comment.pendingPayload || {};
    setPendingComments((prev) =>
      prev.map((item) =>
        item.id === comment.id ? { ...item, status: "editing" } : item
      )
    );
    const start = Number(String(payload.timecode ?? "").split(" - ")[0]);
    if (videoControls?.seek && Number.isFinite(start) && start >= 0) {
      videoControls.seek(start);
    }

    const mentionIds = payload.mentions || [];
    setEditPrefill({
      content: payload.content ?? comment.content,
      tags: (tagsArray || []).filter((tag) =>
        (payload.tagIds || []).includes(tag.id)
      ),
      mentions: (projectUsers || []).filter((u) =>
        mentionIds.includes(u.userId || u.id)
      ),
      attachments: (payload.attachments || []).map((a) => ({
        mediaId: a.mediaId,
      })),
      markerPoint: payload.markerPoint || null,
      drawing: payload.drawing || null,
      pendingId: comment.id,
      isPendingRestore: true,
      savedAt: Date.now(),
    });
  };

  const onCancelPendingEdit = (localId) => {
    setPendingComments((prev) =>
      prev.map((item) =>
        item.id === localId ? { ...item, status: "failed" } : item
      )
    );
    setEditPrefill(null);
  };

  const handleDeleteComment = async (commentId) => {
    const commentIndex = commentsArray.findIndex((com) => com.id === commentId);
    const commentObject = commentsArray[commentIndex];

    const res = await dispatch(deleteVersionComment({ versionId, commentId }));
    if (!res.error) {
      const newCommentsArray = [...commentsArray];
      newCommentsArray.splice(commentIndex, 1, {
        ...commentObject,
        isMarkedForArchive: true,
      });
      setCommentsArray(newCommentsArray);
      calculateCurrentTags(newCommentsArray);

      let newReplies = {};
      for (let i = 0; i < newCommentsArray.length; i++) {
        let commentId = newCommentsArray[i].id;
        const result = await dispatch(fetchCommentReplies(commentId));
        if (!result.err) {
          const commentReplies = result.payload;
          newReplies[commentId] = commentReplies;
        }
      }
      setReplies(newReplies);
    }
    return res;
  };

  const handleRestoreComment = (commentId) => {
    if (commentsFilter === "all") {
      dispatch(restoreVersionComment({ versionId, commentId })).then((res) => {
        if (!res.error) {
          const newCommentsArray = [...commentsArray];
          const commentIndex = newCommentsArray.findIndex(
            (com) => com.id === commentId
          );
          newCommentsArray[commentIndex] = {
            ...newCommentsArray[commentIndex],
            isMarkedForArchive: false,
          };
          setCommentsArray(newCommentsArray);
        }
      });
      return;
    }
    setCommentsFilter("all");
    dispatch(restoreVersionComment({ versionId, commentId })).then((res) => {
      if (!res.error) {
        dispatch(
          fetchVersionComments({
            versionId,
            isDeleted: false,
          })
        ).then((result) => {
          if (!result.error) {
            setCommentsArray(result.payload);
            calculateCurrentTags(result.payload);
          }
        });
      }
    });
  };

  const handleForceDeleteComment = async (commentId) => {
    const res = await dispatch(
      forceDeleteVersionComment({ versionId, commentId })
    );

    if (!res.error) {
      const res2 = await dispatch(
        fetchVersionComments({
          versionId,
          isDeleted: commentsFilter === "archived",
        })
      );

      if (!res2.error) {
        setCommentsArray(res2.payload);
        calculateCurrentTags(res2.payload);
        const newCommentsArray = res2.payload;
        let newReplies = {};
        for (let i = 0; i < newCommentsArray.length; i++) {
          let commentId = newCommentsArray[i].id;
          const result = await dispatch(fetchCommentReplies(commentId));
          if (!result.err) {
            const commentReplies = result.payload;
            newReplies[commentId] = commentReplies;
          }
        }
        setReplies(newReplies);
      }
    }

    return res;
  };

  const renderUsersBlock = () => {
    let sortedOrganizationUsers = [...projectUsers];
    sortedOrganizationUsers = sortedOrganizationUsers
      .sort((a, b) => (a.role === "OWNER" ? 1 : -1))
      .slice(-3);
    if (isMobile) {
      sortedOrganizationUsers = sortedOrganizationUsers[0]
        ? [sortedOrganizationUsers[0]]
        : [];
    }

    return (
      <div className="project-users-row">
        {sortedOrganizationUsers.map((userItem, index) => (
          <div
            className="project-user-avatar-wrapper"
            style={{ right: isMobile ? -13 : `${index * 25}px` }}
          >
            <Avatar name={userItem.email} size={32} />
          </div>
        ))}
      </div>
    );
  };

  const handleRemoveCommentFromMarked = (commentId) => {
    const newCommentsArray = commentsArray.filter((com) => {
      if (com.id !== commentId) return true;
      return !com.isMarkedForArchive;
    });
    setCommentsArray(newCommentsArray);
  };

  const handleUpdateTeamMembers = () => {
    let projectId = video.projectId;
    dispatch(fetchProjectUsers(projectId)).then((res) => {
      if (!res.error) {
        let usersArray = res.payload;
        var unique = usersArray.filter(
          (value, index, array) =>
            array.findIndex((val) => val.email === value.email) === index
        );
        setProjectUsers(unique);
      }
    });
  };

  return (
    <div className={isMobile ? "App-mobile" : "App"}>
      {isMobile && (
        <div
          className={isMobile ? "videoPageHeader-mobile" : "videoPageHeader"}
          style={{ justifyContent: isShared ? "center" : "space-between" }}
        >
          {(!isShared && !isViewer) || (isViewer && isSharedChild) ? (
            <BackButton
              onClick={() => {
                if (isViewer && isSharedChild) {
                  navigate(-1);
                  return;
                }
                if (redirectedFromShare) {
                  navigate("/home");
                } else {
                  navigate(-1);
                }
              }}
            />
          ) : null}
          {isViewer && <div style={{ width: 5, height: 5 }}></div>}
          {isShared && !isViewer && (
            <div style={{ display: "flex", position: "absolute", left: 20 }}>
              {!isMobile && (
                <span
                  style={{
                    color: "white",
                    width: 250,
                    fontSize: 14,
                    textAlign: "left",
                    fontFamily: "NT Somic",
                    fontWeight: 500,
                  }}
                >
                  Войдите или зарегистрируйтесь, чтобы поучаствовать в
                  обсуждении
                </span>
              )}
              <button
                style={{
                  backgroundColor: "#4477ff",
                  border: "none",
                  padding: 10,
                  color: "white",
                  marginLeft: 15,
                  borderRadius: 80,
                  cursor: "pointer",
                }}
                onClick={() => {
                  localStorage.setItem("redirectUrl", `/video/${versionId}`);
                  navigate("/");
                }}
              >
                Войти
              </button>
            </div>
          )}

          <div style={{ position: "relative" }}>
            <div
              className="video-page-header-title"
              style={{
                textAlign: "center",
                color: "white",
                fontSize: "16px",
                fontWeight: 500,
                fontFamily: "NT Somic, sans-serif",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                cursor: "pointer",
                gap: "8px",
                position: "relative",
                zIndex: 2,
                paddingRight: isMobile ? 15 : 0,
              }}
              onClick={() => {
                if (!isShared && !isCommenterRole && !isViewerRole) {
                  setSharePopupOpen((v) => !v);
                }
              }}
            >
              {getVideoTitle()}
              {!isShared && !isMobile && !isCommenterRole && !isViewerRole && (
                <div>
                  <span>Поделиться</span>
                  <img
                    className="video-page-header-img"
                    src={shareIcon}
                    alt="arrow"
                    style={{
                      width: 20,
                      height: 20,
                    }}
                  />
                </div>
              )}
            </div>
            {isSharePopupOpen && (
              <SharePopup
                video={video}
                updateVideo={updateVideo}
                onClose={() => setSharePopupOpen(false)}
                onOpenBacklog={() => setBacklogPopupOpen(true)}
                className="share-popup-context-menu-absolute"
              />
            )}
          </div>
          {isBacklogPopupOpen && (
            <BacklogPopup
              isOpen={isBacklogPopupOpen}
              onClose={() => setBacklogPopupOpen(false)}
            />
          )}
          {isSharedNeedLoginVisible && (
            <InfoModal
              actionText={"Войти"}
              action={() => {
                localStorage.setItem("redirectUrl", `/video/${versionId}`);
                window.location.href = window.location.origin;
              }}
              title="Необходим вход"
              text="Войдите или зарегистрируйтесь, чтобы поучаствовать в обсуждении"
              isOpen={isSharedNeedLoginVisible}
              onClose={() => setIsSharedNeedLoginVisible(false)}
            />
          )}
          {isUsersPopupOpen && (
            <UsersPopup
              video={video}
              projectInfo={video}
              isViewer={isViewerRole}
              isCommenter={isCommenterRole}
              isOpen={isUsersPopupOpen}
              onClose={() => setUsersPopupOpen(false)}
              usersArray={projectUsers}
              updateUsersArray={handleUpdateTeamMembers}
              organizationId={
                video?.organizationId || sharedOrgId || currentOrganization?.id
              }
            />
          )}
          {!isShared && (
            <div
              style={{
                textAlign: "center",
                color: "white",
                fontSize: "16px",
                height: "100%",
                fontWeight: 500,
                fontFamily: "NT Somic, sans-serif",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                cursor: "pointer",
                gap: "8px",
              }}
              onClick={() => setUsersPopupOpen((v) => !v)}
            >
              {renderUsersBlock()}
            </div>
          )}
        </div>
      )}
      <div
        className={`${isMobile ? "mainContent-mobile" : "mainContent"} ${
          isWideMode ? "wide-mode" : ""
        }`}
      >
        <div
          className={isMobile ? "leftContent-mobile" : "leftContent"}
          ref={fullscreenRef}
        >
          {!isWideMode && !isMobile && (
            <div
              className={
                isMobile ? "videoPageHeader-mobile" : "videoPageHeader"
              }
              style={{
                justifyContent:
                  !isShared || (isViewer && isSharedChild)
                    ? "space-between"
                    : "center",
              }}
            >
              {((!isShared && !isViewer) || (isViewer && isSharedChild)) && (
                <BackButton
                  onClick={(e) => {
                    if (isViewer && isSharedChild) {
                      if (video && video.path && video.path.length) {
                        const parentId = video.path[0].id;
                        if (parentId) navigate(`/projects/${parentId}`);
                      } else {
                        navigate(-1, { replace: true });
                      }
                      return;
                    }
                    if (redirectedFromShare) {
                      navigate("/home");
                    } else {
                      if (video && video.path && video.path.length) {
                        const parentId = video.path[0].id;
                        if (parentId) navigate(`/projects/${parentId}`);
                      } else {
                        navigate(-1, { replace: true });
                      }
                    }
                  }}
                />
              )}
              {isViewer && !isSharedChild && (
                <div style={{ width: 5, height: 5 }}></div>
              )}
              {isShared && !isViewer && (
                <div
                  style={{ display: "flex", position: "absolute", left: 20 }}
                >
                  {!isMobile && (
                    <span
                      style={{
                        color: "white",
                        width: 250,
                        fontSize: 14,
                        textAlign: "left",
                        fontFamily: "NT Somic",
                        fontWeight: 500,
                      }}
                    >
                      Войдите или зарегистрируйтесь, чтобы поучаствовать в
                      обсуждении
                    </span>
                  )}

                  <button
                    style={{
                      backgroundColor: "#4477ff",
                      border: "none",
                      padding: 10,
                      color: "white",
                      marginLeft: 15,
                      borderRadius: 80,
                      cursor: "pointer",
                    }}
                    onClick={() => {
                      localStorage.setItem(
                        "redirectUrl",
                        `/video/${versionId}`
                      );
                      navigate("/");
                    }}
                  >
                    Войти
                  </button>
                </div>
              )}

              <div style={{ position: "relative" }}>
                <div
                  className="video-page-header-title"
                  style={{
                    textAlign: "center",
                    color: "white",
                    fontSize: "16px",
                    fontWeight: 500,
                    fontFamily: "NT Somic, sans-serif",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    cursor: "pointer",
                    gap: "8px",
                    position: "relative",
                    zIndex: 2,
                    paddingRight: isMobile ? 12 : 0,
                  }}
                  onClick={() => {
                    if (isViewerRole || isCommenterRole) return;
                    if (!isShared) {
                      setSharePopupOpen((v) => !v);
                    }
                  }}
                >
                  {getVideoTitle()}
                  {!isShared &&
                    !isMobile &&
                    !isViewerRole &&
                    !isCommenterRole && (
                      <div
                        className="video-share-button-block"
                        style={{
                          display: "flex",
                          flexDirection: "row",
                          alignItems: "center",
                          justifyContent: "center",
                          gap: 8,
                          padding: 10,
                          paddingTop: 5,
                          paddingBottom: 5,
                          borderRadius: 8,
                        }}
                      >
                        <span
                          className="video-page-share-label-text"
                          style={{ fontSize: 14, fontWeight: 500 }}
                        >
                          Поделиться
                        </span>
                        <img
                          className="video-page-header-img"
                          src={shareIcon}
                          alt="arrow"
                          style={{
                            width: 20,
                            height: 20,
                          }}
                        />
                      </div>
                    )}
                </div>
                {isSharePopupOpen && (
                  <SharePopup
                    video={video}
                    updateVideo={updateVideo}
                    onClose={() => setSharePopupOpen(false)}
                    onOpenBacklog={() => setBacklogPopupOpen(true)}
                    className="share-popup-context-menu-absolute"
                  />
                )}
              </div>
              {isBacklogPopupOpen && (
                <BacklogPopup
                  isOpen={isBacklogPopupOpen}
                  onClose={() => setBacklogPopupOpen(false)}
                />
              )}
              {isSharedNeedLoginVisible && (
                <InfoModal
                  actionText={"Войти"}
                  action={() => {
                    localStorage.setItem("redirectUrl", `/video/${versionId}`);
                    window.location.href = window.location.origin;
                  }}
                  title="Необходим вход"
                  text="Войдите или зарегистрируйтесь, чтобы поучаствовать в обсуждении"
                  isOpen={isSharedNeedLoginVisible}
                  onClose={() => setIsSharedNeedLoginVisible(false)}
                />
              )}
              {isUsersPopupOpen && (
                <UsersPopup
                  video={video}
                  projectInfo={video}
                  isViewer={isViewerRole}
                  isCommenter={isCommenterRole}
                  isOpen={isUsersPopupOpen}
                  onClose={() => setUsersPopupOpen(false)}
                  usersArray={projectUsers}
                  updateUsersArray={handleUpdateTeamMembers}
                  organizationId={
                    video?.organizationId ||
                    sharedOrgId ||
                    currentOrganization?.id
                  }
                />
              )}
              {(!isShared && (
                <div
                  style={{
                    textAlign: "center",
                    color: "white",
                    fontSize: "16px",
                    height: "100%",
                    fontWeight: 500,
                    fontFamily: "NT Somic, sans-serif",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    cursor: "pointer",
                    gap: "8px",
                  }}
                  onClick={() => setUsersPopupOpen((v) => !v)}
                >
                  {renderUsersBlock()}
                </div>
              )) ||
                null}
              {isViewer && isSharedChild && (
                <div style={{ width: 56, height: 5 }}></div>
              )}
            </div>
          )}
          <div className="videoSection">
            <div className="videoPlayerContainer">
              <VideoWithHLS
                setPlaybackStarted={setPlaybackStarted}
                ref={playerRef}
                hlsUrl={video.hlsUrl}
                isVideoWaiting={isVideoWaiting}
                setIsVideoWaiting={setIsVideoWaiting}
                onTimeUpdate={handleTimeUpdate}
                onChangeSpeed={(speedValue) => setSpeed(speedValue)}
                onPlayStarted={() => setActiveAnnotation(null)}
                onDurationChange={handleDurationChange}
                isWideMode={isWideMode}
                annotation={shownAnnotation}
                annotationDimmed={!hasDraftAnnotation}
                isLocationMode={isLocationMode}
                fullscreenTarget={fullscreenRef}
                onLocationClick={handleLocationClick}
                isDrawingMode={isDrawingMode}
                draftDrawing={draftDrawing}
                onDraftDrawingChange={setDraftDrawing}
                drawingColor={drawingColor}
                onDrawingColorChange={setDrawingColor}
                onDrawingDone={() => setIsDrawingMode(false)}
                drawingLabels={{
                  undo: t("comments.drawing.undo"),
                  clear: t("comments.drawing.clear"),
                  done: t("comments.drawing.done"),
                }}
                isPlaying={isPlaying}
                setIsPlaying={setIsPlaying}
                overlay={
                  <CommentToast
                    toasts={toasts}
                    onDismiss={dismiss}
                    onHold={hold}
                    onRelease={release}
                    onOpen={(c) => setHighlightCommentId(c.id)}
                    tagsDefaultColors={tagsDefaultColors}
                  />
                }
              />
            </div>
            <div
              className={isMobile ? "videoTimeline-mobile" : "videoTimeline"}
            >
              <VideoTimeline
                selectedTag={selectedTag}
                isViewer={isViewerRole}
                isCommenter={isCommenterRole}
                isPlaying={isPlaying}
                playbackStarted={playbackStarted}
                setPlaybackStarted={setPlaybackStarted}
                newMarkerInfo={newMarkerInfo}
                setNewMarkerInfo={setNewMarkerInfo}
                currentTime={currentTime}
                totalTime={totalTime}
                progress={currentProgress}
                newCommentEnd={newCommentEnd}
                setNewCommentEnd={(timecodeEnd) =>
                  setNewCommentEnd(timecodeEnd)
                }
                videoControls={videoControls}
                isSharePopupOpen={isSharePopupOpen}
                isWideMode={isWideMode}
                onWideModeChange={setIsWideMode}
                comments={mergedComments}
                onCommentMarkerClick={setHighlightCommentId}
                currentHoveredCommentId={currentHoveredCommentId}
                setCurrentHoveredCommentId={setCurrentHoveredCommentId}
                version={video}
                tagsDefaultColors={tagsDefaultColors}
              />
            </div>
          </div>
          <div className="videoControls-buttons">
            <VideoControls
              isPlaying={isPlaying}
              setIsPlaying={setIsPlaying}
              speed={speed}
              setSpeed={setSpeed}
              currentTime={currentTime}
              totalTime={totalTime}
              newMarkerInfo={newMarkerInfo}
              setNewMarkerInfo={setNewMarkerInfo}
              progress={currentProgress}
              videoControls={videoControls}
              isWideMode={isWideMode}
              isSharePopupOpen={isSharePopupOpen}
              isUsersPopupOpen={isUsersPopupOpen}
              onWideModeChange={setIsWideMode}
              comments={mergedComments}
              onCommentMarkerClick={setHighlightCommentId}
              toastsEnabled={toastsEnabled}
              onToastsChange={setToastsEnabled}
              autoScroll={autoScroll}
              onAutoScrollChange={setAutoScroll}
            />
          </div>
        </div>

        {isShowReleaseNotes && (
          <ReleaseNotesModal
            isOpen={isShowReleaseNotes}
            onClose={() => {
              setShowReleaseNotes(false);
              setIsShowReleaseNotes(false);
            }}
            video={video}
          />
        )}
        {!isWideMode && (
          <div
            className={isMobile ? "commentsSection-mobile" : "commentsSection"}
          >
            <CommentsLayout
              tagsDefaultColors={tagsDefaultColors}
              setTagsDefaultColors={setTagsDefaultColors}
              sharedOrgId={sharedOrgId}
              setSharedOrgId={setSharedOrgId}
              replies={replies}
              setReplies={setReplies}
              onRemoveCommentFromMarked={handleRemoveCommentFromMarked}
              totalTime={totalTime}
              newMarkerInfo={newMarkerInfo}
              setNewMarkerInfo={setNewMarkerInfo}
              updateComments={updateComments}
              isSendingComment={isSendingComment}
              commentsFilter={commentsFilter}
              onChangeCommentsFilter={changeCommentsFilter}
              onDeleteComment={handleDeleteComment}
              onForceDeleteComment={handleForceDeleteComment}
              onRestoreComment={handleRestoreComment}
              videoControls={videoControls}
              currentTime={currentTime}
              videoRef={playerRef}
              comments={mergedComments}
              onCommentsChange={setCommentsArray}
              markerPoint={markerPoint}
              onSetMarkerPoint={setMarkerPoint}
              isLocationMode={isLocationMode}
              highlightCommentId={highlightCommentId}
              setIsLocationMode={setIsLocationMode}
              draftDrawing={draftDrawing}
              onSetDraftDrawing={setDraftDrawing}
              isDrawingMode={isDrawingMode}
              setIsDrawingMode={setIsDrawingMode}
              onPressTimestamp={(annotation) => {
                setActiveAnnotation(annotation);
              }}
              onSubmitComment={handleCreateComment}
              onSubmitCommentsList={handleSubmitCommentsList}
              onRetryPendingComment={handleRetryPendingComment}
              onDeletePendingComment={handleDeletePendingComment}
              onEditPendingComment={onEditPendingComment}
              onCancelPendingEdit={onCancelPendingEdit}
              editPrefill={editPrefill}
              isShowUnreplied={isShowUnreplied}
              setIsShowUnreplied={setIsShowUnreplied}
              versionId={versionId}
              projectUsers={projectUsers}
              tagsArray={tagsArray}
              setTagsArray={setTagsArray}
              filteredTags={filteredTags}
              setCurrentHoveredCommentId={setCurrentHoveredCommentId}
              currentHoveredCommentId={currentHoveredCommentId}
              isShared={isShared}
              onShowSharedNeedLogin={() => setIsSharedNeedLoginVisible(true)}
              isViewer={isViewerRole}
              isCommenter={isCommenterRole}
              myProjectRole={myProjectRole}
              newCommentEnd={newCommentEnd}
              setNewCommentEnd={setNewCommentEnd}
              selectedTag={selectedTag}
              setSelectedTag={setSelectedTag}
            />
          </div>
        )}
      </div>
    </div>
  );
}

export default VideoPage;
